import {useState, useRef, useEffect} from 'react'
import {X, Globe, Layers} from 'lucide-react'
import {useAppConfig} from '@/hooks/use-app-config'
import type {AppConfig, LocalizedTypeValue} from '@/hooks/use-app-config'
import {useI18n} from '@/i18n'
import {Checkbox} from '@/components/ui/checkbox'
import {Button} from '@/components/ui/button'
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover'
import {Badge} from '@/components/ui/badge'
import {cn} from '@/lib/utils'
import {AttributeEntityType} from '@/types/attribute-entity-type'

interface TypeScopeSelectorProps {
    entityType: AttributeEntityType
    value: string[]
    onChange: (codes: string[]) => void
    disabled?: boolean
    className?: string
}

const ENTITY_TYPE_KEY: Record<string, keyof AppConfig> = {
    [AttributeEntityType.IDENTITY]: 'identityTypes',
    [AttributeEntityType.PARTY]: 'partyTypes',
    [AttributeEntityType.RESOURCE]: 'resourceTypes',
    [AttributeEntityType.FUNCTION_GROUP]: 'functionGroupTypes',
    [AttributeEntityType.TARGET_SYSTEM]: 'systemTypes',
    [AttributeEntityType.CONNECTOR]: 'connectorTypes',
}

function getTypes(entityType: AttributeEntityType, config: ReturnType<typeof useAppConfig>): LocalizedTypeValue[] {
    const key = ENTITY_TYPE_KEY[entityType]
    if (!key) throw new Error(`TypeScopeSelector: no config key mapping for entityType "${entityType}"`)
    const value = config[key]
    return Array.isArray(value) ? value as LocalizedTypeValue[] : []
}

export function TypeScopeSelector({entityType, value, onChange, disabled, className}: TypeScopeSelectorProps) {
    const i18n = useI18n()
    const config = useAppConfig()
    const [popoverOpen, setPopoverOpen] = useState(false)
    const popoverRef = useRef<HTMLDivElement>(null)

    const types = getTypes(entityType, config)
    const isAll = value.length === 0
    const selectedTypes = types.filter(t => value.includes(t.code))

    // Close popover when clicking outside
    useEffect(() => {
        if (!popoverOpen) return
        function handleClickOutside(event: MouseEvent) {
            if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
                setPopoverOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [popoverOpen])

    function handleToggleAll() {
        onChange([])
        setPopoverOpen(false)
    }

    function handleToggleType(code: string) {
        if (value.includes(code)) {
            onChange(value.filter(c => c !== code))
        } else {
            onChange([...value, code])
        }
    }

    function handleRemoveType(code: string, e: React.MouseEvent) {
        e.stopPropagation()
        onChange(value.filter(c => c !== code))
    }

    if (types.length === 0) return null

    return (
        <div className={cn('flex flex-col gap-2', className)}>
            {/* Segmented toggle */}
            <div className="flex rounded-lg border bg-muted p-0.5 gap-0.5 w-fit">
                <button
                    type="button"
                    onClick={handleToggleAll}
                    disabled={disabled}
                    data-testid="type-scope-all"
                    className={cn(
                        'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                        isAll
                            ? 'bg-background text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted/80'
                    )}
                >
                    <Globe className="h-3.5 w-3.5" />
                    {i18n.attributeDefinitions.typeScopeAllTypes}
                </button>
                <button
                    type="button"
                    onClick={() => setPopoverOpen(!popoverOpen)}
                    disabled={disabled}
                    data-testid="type-scope-specific"
                    className={cn(
                        'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                        !isAll
                            ? 'bg-background text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted/80'
                    )}
                >
                    <Layers className="h-3.5 w-3.5" />
                    {i18n.attributeDefinitions.typeScopeSpecificTypes}
                    {!isAll && (
                        <span className="ml-1 bg-primary/10 text-primary text-xs px-1.5 py-0.5 rounded-full">
                            {value.length}
                        </span>
                    )}
                </button>
            </div>

            {/* Selected type pills + dropdown trigger — show when specific types selected OR popover is open */}
            {(!isAll || popoverOpen) && (
                <div className="flex flex-wrap items-center gap-2">
                    {selectedTypes.map(type => (
                        <Badge
                            key={type.code}
                            variant="secondary"
                            className="pl-2.5 pr-1.5 py-1 gap-1.5 text-sm font-normal"
                        >
                            {type.label}
                            <button
                                type="button"
                                onClick={(e) => handleRemoveType(type.code, e)}
                                disabled={disabled}
                                className="ml-0.5 rounded-full hover:bg-muted p-0.5 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                title={i18n.common.remove}
                                data-testid={`type-scope-remove-${type.code}`}
                            >
                                <X className="h-3 w-3 text-muted-foreground" />
                            </button>
                        </Badge>
                    ))}
                    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={disabled}
                                className="h-7 gap-1 text-xs py-0"
                                onClick={() => setPopoverOpen(!popoverOpen)}
                                data-testid="type-scope-add"
                            >
                                <span className="text-muted-foreground">+</span>
                                {i18n.attributeDefinitions.typeScopeAddType}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent ref={popoverRef} className="w-64 p-0" align="start">
                            <div className="p-2 border-b bg-muted/50">
                                <p className="text-xs font-medium text-muted-foreground">
                                    {i18n.attributeDefinitions.typeScopeSelectTypes}
                                </p>
                            </div>
                            <div className="max-h-64 overflow-y-auto py-1">
                                {types.map(type => (
                                    <label
                                        key={type.code}
                                        className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-muted/50 transition-colors"
                                    >
                                        <Checkbox
                                            checked={value.includes(type.code)}
                                            onCheckedChange={() => handleToggleType(type.code)}
                                            disabled={disabled}
                                            data-testid={`type-scope-checkbox-${type.code}`}
                                        />
                                        <span className="text-sm flex-1">{type.label}</span>
                                        <span className="text-xs text-muted-foreground font-mono">{type.code}</span>
                                    </label>
                                ))}
                            </div>
                        </PopoverContent>
                    </Popover>
                </div>
            )}
        </div>
    )
}
